import Flex from 'components/Layout/components/Flex';
import type { ProductListPartialViewModel } from 'autogen/swagger/all';
import { useRef } from 'react';
import { useTransition } from '@react-spring/web';
import isClient from 'helpers/isClient';
import styled from 'styled-components';
import ActiveFiltersRenderer from './components/ActiveFilters/ActiveFiltersRenderer';
import FilterMenuRenderer from './components/Filters/FilterMenuRenderer';
import StaticFilterContent from './components/StaticFilterContent';
import StickyFilterHeader from './components/StickyFilterHeader';
import useShouldFiltersBeSticky from './shared/hooks/useShouldFiltersBeSticky';
import { useRecoilValue } from 'recoil';
import { searchActiveAtom } from 'components/QuickSearch/atoms/quickViewAtoms';
import { SearchTextContext } from 'components/pages/Search/hooks/contexts';

/* #region styles */
const FiltersContainer = styled(Flex.Column)`
    min-height: 48px;
`;
/* #endregion */

export default function ProductListFilters({ model }: { model: ProductListPartialViewModel }) {
    const { productList, searchText } = model;
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const shouldFiltersBeSticky = useShouldFiltersBeSticky(filterContainerRef);
    const isQuickSearchActive = useRecoilValue(searchActiveAtom);

    const transition = useTransition(shouldFiltersBeSticky || isQuickSearchActive, {
        from: (isStickyFilters) => (isStickyFilters ? { y: '-100%' } : { opacity: 0 }),
        enter: (isStickyFilters) => (isStickyFilters ? { y: '0%' } : { opacity: 1 }),
        leave: (isStickyFilters) => (isStickyFilters ? { y: '-100%' } : { opacity: 0 }),
        initial: (isStickyFilters) => (isStickyFilters ? undefined : false),
        expires: false
    });

    return (
        <FiltersContainer ref={filterContainerRef}>
            {/* Renders all filter menus (smartviews) that can be opened */}
            <FilterMenuRenderer model={model} />

            {/* This Transition Switches between the static and sticky filters when scrolled */}
            {transition((styles, isStickyFilters) =>
                isStickyFilters ? (
                    <SearchTextContext.Provider value={{ searchText }}>
                        <StickyFilterHeader model={productList} style={{ ...styles }} />
                    </SearchTextContext.Provider>
                ) : (
                    <StaticFilterContent model={productList} style={isClient ? { ...styles } : { opacity: ' ' }} /> // react spring ssr fix
                )
            )}

            {/* Renders all active filter tags */}
            <ActiveFiltersRenderer model={productList} />
        </FiltersContainer>
    );
}
